import React, { useEffect, useRef, useState } from "react";
import { addDays, endOfWeek, startOfDay, startOfWeek } from "date-fns";
import { useRangePickerState } from "./useRangePickerState";
import { isEqualDate, isInRange } from "./util";
import { styled } from "src/ccl";
import { AvailableIcon, Icon, Text } from "src/ccl/document";
import { Flex, Box, Grid } from "src/ccl/layout";
import { formatDate, formatDisplayDate } from "src/utils/dates";
import { DateRangeInput } from "src/graphql/types";
import { EnhancedInput } from "src/ccl/data-entry/input";
import { useDatePickerState } from "src/ccl/data-entry/datePicker/useDatePickerState";
import { OutsideClickHandler } from "src/ccl/util/OutsideClickHandler";

const dayTitles = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

interface CalendarHeaderProps {
  startDate?: string;
  endDate?: string;
}

const CalendarHeader = ({ startDate, endDate }: CalendarHeaderProps) => (
  <>
    <Flex css={{ justifyContent: "space-between" }}>
      <Text color="grey6" variant="meta">
        Start
      </Text>
      <Text color="grey6" variant="meta">
        End
      </Text>
    </Flex>
    <Grid
      css={{
        justifyContent: "space-between",
        pt: "$3",
        gridColumns: "1fr 1fr 1fr",
        pb: "$10",
      }}
    >
      <Text variant="meta" css={{ fontWeight: "$medium" }}>
        {startDate || "Select Date"}
      </Text>
      <Text variant="meta" css={{ textAlign: "center" }}>
        To
      </Text>
      <Text
        color={endDate ? "black" : "grey6"}
        variant="meta"
        css={{ fontWeight: "$medium", textAlign: "right" }}
      >
        {endDate || "Select Date"}
      </Text>
    </Grid>
  </>
);

interface CalendarControlProps {
  variant: AvailableIcon;
  onClick: () => void;
}

const CalendarControl = ({ variant, onClick }: CalendarControlProps) => (
  <Box
    as="button"
    type="button"
    aria-label={variant === "chevronLeft" ? "Previous month" : "Next month"}
    onClick={onClick}
    css={{
      my: "auto",
      border: 0,
      background: "transparent",
      padding: "$2",
      "&:focus-visible": { outline: "2px solid $black", outlineOffset: 2 },
      "&:hover": { cursor: "pointer" },
      justifySelf: variant === "chevronRight" ? "end" : "start",
    }}
  >
    <Icon variant={variant} size={18} />
  </Box>
);

const Day = styled("button", {
  border: 0,
  backgroundColor: "transparent",
  color: "$black",
  padding: 0,
  fontFamily: "inherit",
  textAlign: "center",
  py: "$2",
  fontSize: "$14",
  lineHeight: "$22",
  fontWeight: "$bold",
  borderRadius: "$round",
  width: "$9",
  height: "$9",
  maxWidth: "100%",
  "&:focus-visible": { outline: "2px solid $black", outlineOffset: 2 },
  "&:hover": {
    cursor: "pointer",
    backgroundColor: "$grey3",
  },
  variants: {
    variant: {
      selected: {
        backgroundColor: "$black",
        color: "$white",
        "&:hover": {
          color: "$black",
        },
      },
      inRange: {
        backgroundColor: "$grey2",
      },
      disabled: {
        color: "$grey3",
        "&:hover": {
          backgroundColor: "initial",
          cursor: "not-allowed",
        },
      },
    },
  },
});

interface CellProps {
  isStart: boolean;
  isEnd: boolean;
  inRange: boolean;
  index: number;
  day?: number;
  onClick?: () => void;
  disabled?: boolean;
  dateLabel?: string;
  tabIndex?: number;
  buttonRef?: (button: HTMLButtonElement | null) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
}

const Cell = ({
  isStart,
  isEnd,
  inRange,
  index,
  day,
  onClick,
  disabled,
  dateLabel,
  tabIndex,
  buttonRef,
  onKeyDown,
}: CellProps) => {
  const selected = isStart || isEnd;

  const isStartOfRow = index % 7 === 0 || day === 1 || isStart;
  const isEndOfRow = index % 7 === 6 || isEnd;

  // being the start of the range overrides being the end of a row
  const overrideFillLeft = isEndOfRow && isStart;
  const fillLeft = !overrideFillLeft && inRange && isEndOfRow;
  const fillRight = inRange && isStartOfRow;
  const linearGradient = fillLeft
    ? `to left, $white 50%, $grey2 50%`
    : fillRight
    ? `to right, $white 50%, $grey2 50%`
    : undefined;

  // we round the edges when we're at the start of the month end of the
  //month, start of a row or end of a row,
  const roundEdges = fillLeft || fillRight;
  return (
    <Flex
      css={{
        justifyContent: "space-around",
        backgroundColor: !selected && inRange ? "$grey2" : undefined,
        linearGradient,
      }}
    >
      {day && (
        <Day
          type="button"
          disabled={disabled}
          aria-label={dateLabel}
          aria-pressed={selected || inRange}
          tabIndex={tabIndex}
          ref={buttonRef}
          onKeyDown={onKeyDown}
          onClick={onClick}
          variant={
            selected
              ? "selected"
              : roundEdges
              ? "inRange"
              : disabled
              ? "disabled"
              : undefined
          }
        >
          {day}
        </Day>
      )}
    </Flex>
  );
};

interface RangePickerProps {
  onChange: (dateRange: DateRangeInput) => void;
  value?: DateRangeInput;
}

export const RangePicker = (props: RangePickerProps) => {
  const { startDate, endDate, isDraft, applied, ...rangePickerProps } =
    useRangePickerState(props.onChange, props.value, "value" in props);

  return (
    <Box css={{ pt: "$6", mt: "$7", borderTop: "1px solid $grey1" }}>
      {isDraft && applied && (
        <Text variant="meta" css={{ pb: "$4" }}>
          Currently applied: {formatDate(applied.startDate, "dd MMM yyyy")} –{" "}
          {formatDate(applied.endDate, "dd MMM yyyy")}
        </Text>
      )}
      <Text
        variant="meta"
        aria-live="polite"
        css={{ pb: "$4", color: "$grey6" }}
      >
        {isDraft
          ? "Select an end date"
          : "Select a start date, then an end date"}
      </Text>
      <CalendarHeader
        startDate={startDate ? formatDate(startDate, "dd MMM yyyy") : undefined}
        endDate={endDate ? formatDate(endDate, "dd MMM yyyy") : undefined}
      />
      <CalendarBody
        startDate={startDate}
        endDate={endDate}
        {...rangePickerProps}
      />
    </Box>
  );
};

interface DatePickerProps {
  onChange: (date: Date) => void;
  disablePastDates?: boolean;
  initialDate?: Date;
  disabled?: boolean;
  calendarPosition?: "top" | "bottom";
  startDate?: Date;
  endDate?: Date;
  rangeDate?: boolean;
  value?: Date | null;
  id?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  responsive?: boolean;
}

export const DatePicker = ({
  onChange,
  rangeDate = false,
  disablePastDates,
  initialDate,
  endDate,
  disabled,
  startDate,
  calendarPosition = "top",
  value,
  id,
  "aria-label": label,
  "aria-describedby": describedBy,
  responsive = false,
}: DatePickerProps) => {
  const root = useRef<HTMLDivElement>(null);
  const suppressFocus = useRef(false);
  const closeCalendar = () => {
    setShowCalendar(false);
    suppressFocus.current = true;
    root.current?.querySelector("input")?.focus();
    suppressFocus.current = false;
  };
  const { date, ...datePickerProps } = useDatePickerState(
    (d) => {
      closeCalendar();
      onChange(d);
    },
    initialDate,
    value,
  );
  const [showCalendar, setShowCalendar] = useState(false);

  return (
    <OutsideClickHandler onOutsideClick={() => setShowCalendar(false)}>
      <Box
        ref={root}
        onBlur={(event) => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setShowCalendar(false);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && showCalendar) {
            event.preventDefault();
            event.stopPropagation();
            closeCalendar();
          }
        }}
      >
        <Box>
          <EnhancedInput
            type="text"
            id={id}
            aria-label={label}
            aria-describedby={describedBy}
            aria-haspopup="dialog"
            aria-expanded={showCalendar && !disabled}
            readOnly
            disabled={disabled}
            value={
              value === null
                ? ""
                : formatDisplayDate(value || date, "dd MMM yyyy")
            }
            placeholder="Choose date"
            onFocus={() => {
              if (!disabled && !suppressFocus.current) setShowCalendar(true);
            }}
            onClick={() => !disabled && setShowCalendar(true)}
            onKeyDown={(event) => {
              if (["Enter", " ", "ArrowDown"].includes(event.key)) {
                event.preventDefault();
                if (!disabled) setShowCalendar(true);
              }
            }}
            inputSuffix={<Icon variant="calendar" size={14} color="grey6" />}
            suffixCss={{ pointerEvents: "none" }}
            onChange={() => {}}
            variant="rebrand"
            css={{
              cursor: "pointer",
              alignItems: "center",
            }}
          />
        </Box>

        <Box css={{ position: "relative" }}>
          <Box
            role="dialog"
            hidden={!showCalendar || disabled}
            aria-label={label ? `${label} calendar` : "Choose date calendar"}
            css={{
              zIndex: "$500",
              position: "absolute",
              [calendarPosition === "top" ? "bottom" : "top"]: "$14",
              left: "$0",
              px: "$11",
              py: "$9",
              backgroundColor: "$white",
              display: showCalendar && !disabled ? "initial" : "none",
              boxShadow: "$subtle",
              ...(responsive
                ? {
                    width: "100%",
                    "@bp2": { width: "min(340px, calc(100vw - 48px))" },
                    boxSizing: "border-box",
                    px: "$5",
                    [calendarPosition === "top" ? "bottom" : "top"]: "$3",
                  }
                : {}),
            }}
          >
            <CalendarBody
              startDate={
                startDate || (value === null ? undefined : value || date)
              }
              endDate={endDate}
              disablePastDates={disablePastDates}
              rangeDate={rangeDate}
              {...datePickerProps}
            />
          </Box>
        </Box>
      </Box>
    </OutsideClickHandler>
  );
};

interface CalendarBodyProps {
  previousMonth: () => void;
  nextMonth: () => void;
  setDate: (d: number) => void;
  daysOfMonth: (number | undefined)[];
  displayedMonth: Date;
  startDate?: Date;
  endDate?: Date;
  showMonth?: (date: Date) => void;
  disablePastDates?: boolean;
  rangeDate?: boolean;
}

export const CalendarBody = ({
  previousMonth,
  nextMonth,
  setDate,
  daysOfMonth,
  displayedMonth,
  startDate,
  endDate,
  showMonth,
  disablePastDates,
  rangeDate = false,
}: CalendarBodyProps) => {
  const [focusedDate, setFocusedDate] = useState(
    startDate ?? startOfDay(new Date()),
  );
  const dayButtons = useRef<Record<string, HTMLButtonElement | null>>({});
  const requestedFocus = useRef<string>();
  const monthKey = formatDate(displayedMonth, "yyyy-MM");
  const focusedKey = formatDate(focusedDate, "yyyy-MM-dd");
  const isDateDisabled = (date: Date) =>
    !!(
      (disablePastDates && date < startOfDay(new Date())) ||
      (rangeDate && startDate && date < startDate) ||
      (rangeDate && endDate && date > endDate)
    );
  const firstEnabledDay = daysOfMonth.find(
    (day) =>
      day !== undefined &&
      !isDateDisabled(
        new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), day),
      ),
  );
  const tabDate =
    formatDate(focusedDate, "yyyy-MM") === monthKey &&
    !isDateDisabled(focusedDate)
      ? focusedKey
      : firstEnabledDay !== undefined
      ? formatDate(
          new Date(
            displayedMonth.getFullYear(),
            displayedMonth.getMonth(),
            firstEnabledDay,
          ),
          "yyyy-MM-dd",
        )
      : undefined;

  useEffect(() => {
    if (requestedFocus.current && dayButtons.current[requestedFocus.current]) {
      dayButtons.current[requestedFocus.current]?.focus();
      requestedFocus.current = undefined;
    }
  }, [monthKey, focusedKey]);

  const navigateDate = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    date: Date,
  ) => {
    let target: Date;
    switch (event.key) {
      case "ArrowLeft":
        target = addDays(date, -1);
        break;
      case "ArrowRight":
        target = addDays(date, 1);
        break;
      case "ArrowUp":
        target = addDays(date, -7);
        break;
      case "ArrowDown":
        target = addDays(date, 7);
        break;
      case "Home":
        target = startOfWeek(date);
        break;
      case "End":
        target = startOfDay(endOfWeek(date));
        break;
      default:
        return;
    }
    event.preventDefault();
    if (isDateDisabled(target)) return;
    requestedFocus.current = formatDate(target, "yyyy-MM-dd");
    setFocusedDate(target);
    if (formatDate(target, "yyyy-MM") !== monthKey) {
      if (showMonth) showMonth(target);
      else if (target < displayedMonth) previousMonth();
      else nextMonth();
    }
  };

  return (
    <>
      <Grid
        css={{
          justifyContent: "space-between",
          gridColumns: "40px minmax(0, 1fr) 40px",
          pb: "$7",
        }}
      >
        <CalendarControl variant="chevronLeft" onClick={previousMonth} />
        <Text css={{ fontWeight: "$bold", textAlign: "center" }}>
          {formatDate(displayedMonth, "MMMM yyyy")}
        </Text>
        <CalendarControl variant="chevronRight" onClick={nextMonth} />
      </Grid>
      <Grid
        css={{
          gridColumns: "repeat(7, minmax(0, 1fr))",
          textAlign: "center",
          rowGap: 9,
        }}
      >
        {dayTitles.map((d, i) => (
          <Text
            key={i}
            variant="meta"
            color="grey6"
            css={{ fontWeight: "$bold" }}
          >
            {d}
          </Text>
        ))}

        {daysOfMonth.map((d, i) => {
          const date = d
            ? new Date(
                displayedMonth.getFullYear(),
                displayedMonth.getMonth(),
                d,
              )
            : undefined;
          const dateKey = date ? formatDate(date, "yyyy-MM-dd") : undefined;
          const inRange =
            d &&
            startDate &&
            isInRange(d, new Date(displayedMonth), startDate, endDate);
          return (
            <Cell
              key={i}
              index={i}
              day={d}
              dateLabel={
                date
                  ? formatDate(
                      date,
                      showMonth ? "EEEE, d MMMM yyyy" : "d MMMM yyyy",
                    )
                  : undefined
              }
              tabIndex={dateKey === tabDate ? 0 : -1}
              buttonRef={
                dateKey
                  ? (button) => {
                      if (button) dayButtons.current[dateKey] = button;
                      else delete dayButtons.current[dateKey];
                    }
                  : undefined
              }
              onKeyDown={
                date ? (event) => navigateDate(event, date) : undefined
              }
              onClick={
                d && date
                  ? () => {
                      setFocusedDate(date);
                      setDate(d);
                    }
                  : undefined
              }
              isStart={
                !!(
                  d &&
                  startDate &&
                  isEqualDate(d, new Date(displayedMonth), startDate)
                )
              }
              isEnd={
                !!(
                  d &&
                  endDate &&
                  isEqualDate(d, new Date(displayedMonth), endDate)
                )
              }
              inRange={!!inRange}
              disabled={date ? isDateDisabled(date) : false}
            />
          );
        })}
      </Grid>
    </>
  );
};
